import { AnalyticsController } from 'analytics/analytics-controller';
import type { StandardStatus } from '@zoocasa/go-search';
import { useTenantId } from 'hooks/use-tenant-id';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'types/react';
import { standardStatusToString } from 'types/reso';
import { PropertyViewedSource } from '@bedrock/analytics';
import { toBedrockStandardStatus, toBedrockTenantId } from 'analytics/providers/kinesis/adapters';

/**
 * Tracks a property viewed event when the given listing is viewed.
 *
 * Fires at most once per (listingId, url) per mount.
 */
export function useTrackPropertyView(listingId: number, slug: string, standardStatus: StandardStatus, source: PropertyViewedSource) {
  const router = useRouter();
  const tenantId = useTenantId();
  const lastTrackedKey = useRef<string | null>(null);

  const url = typeof router?.asPath === 'string' ? router.asPath.trim() : '';

  useIsomorphicLayoutEffect(() => {
    if (!listingId || !slug || standardStatus == null || !url || !tenantId) return;

    const key = `${listingId}-${url}`;
    if (lastTrackedKey.current === key) return;

    lastTrackedKey.current = key;
    AnalyticsController.trackPropertyViewed(
      { id: listingId.toString(), slug, standard_status: toBedrockStandardStatus(standardStatusToString(standardStatus)) },
      { url, tenant_id: toBedrockTenantId(tenantId), source }
    );
  }, [listingId, slug, standardStatus, url, tenantId, source]);
}
